<template>
  <div class="owal-report__container">
    <div class="owal-table__container">
      <owal-title-bar
        :left="backButton"
        :right="downloadButton"
        :title="report ? report.metadata.title : 'Reports'"
        @click="itemWasClicked"
      />
      <owal-search-bar
        v-if="reports && reports.length > 0"
        :items="rows"
        :callback="searchChanged"
        :search-keys="searchKeys"
        :table-header="true"
        :sorts="sorts"
        :can-toggle-filters="false"
      />
      <virtual-list
        v-if="reports && reports.length > 0"
        key="report-list"
        :size="33.5"
        :remain="Math.min(numberOfCells, displayRows.length)"
        :bench="10"
        class="report-table"
        rtag="table"
      >
        <tr
          v-for="(row, index) in displayRows"
          :key="index"
          :class="{'report-summary': !report, 'report-data': !!report}"
          @click="!report ? selectReport(row.id) : undefined"
        >
          <div
            v-if="!report"
            class="report-name"
          >
            <report-cell
              v-for="header in rootHeaders"
              :key="`${header.key}-${index}`"
              :content="contentForColumnInRow(header, row)"
            />
          </div>
          <div
            v-if="report"
            style="display: flex; flex-direction: row; flex: 1;"
          >
            <report-cell
              v-for="(field, index) in row"
              :key="`${field}-${index}`"
              :abbreviated="headers.length > 4"
              :content="field"
            />
          </div>
          <!-- <font-awesome-icon icon='chevron-right' v-if='!report' /> -->
        </tr>
      </virtual-list>
      <div
        v-else
        class="no-reports__container"
      >
        There are no reports available at this time.
      </div>
    </div>
  </div>
</template>

<script>
import { OwalSearchBar, OwalTitleBar, OwalSortPredicate, objectAtKeypath } from '@/libraries/owal-ui'
import { SVC_A } from '@/libraries/api/base'
import ReportCell from './ReportCell'
import { mapGetters } from 'vuex'
import { AutoSizingList } from '@/mixins'
import VirtualList from 'vue-virtual-scroll-list'

export default {
  name: 'Report',
  mixins: [AutoSizingList],
  data () {
    return {
      /** All of the possible reports. */
      reports: [],
      /** The current data, after searching. */
      searchResults: undefined,
      reportObj: null,
    }
  },
  computed: {
    ...mapGetters({
      domainUUID: 'currentDomainUUID',
      identityIds: 'credentialMismatch/identityIds',
    }),
    /** The report object that the user is looking at. */
    report () {
      if (this.reportObj) {
        return this.reportObj
      }
      const reportId = this.$route.params.id
      const reportObj = this.reports.find(r => parseInt(reportId, 10) === r.id)
      return reportObj
    },
    /**
     * The headers to show in the search bar.
     */
    headers () {
      if (this.report && this.report.headers) {
        let headers = this.report.headers
        headers.push({ key: 'url', title: 'URL' })
        return headers
      }
      return this.rootHeaders
    },
    /** The rows to input to the search bar. */
    rows () {
      if (this.report && this.report.values) {
        const rows = this.report.values.map(row => {
          row.moreDetailsResource = this.generateRowURL(row)
          return row
        })
        return rows
      }
      return this.reports
    },
    /** The rows used for rendering.
     * Keeping this in its own computed property allows us to
     * pass a dynamic list rows to the search bar without causing
     * an infinite loop.
     *
     * Vue => report OR reports => rows => searchBar => displayRows => UI
     */
    displayRows () {
      let rows = this.rows
      if (this.searchResults) {
        rows = this.searchResults
      }
      return rows
    },
    /** The back button for transitioning from a report to the list of reports. */
    backButton () {
      if (this.report) {
        return '< Back'
      } else {
        return []
      }
    },
    /** The print button */
    downloadButton () {
      if (this.report) {
        return 'Download'
      } else {
        return []
      }
    },
    /** Returns search related headers. */
    searchKeys () {
      const keys = this.report && this.report.headers ? this.report.headers.map(header => header.key) : ['metadata.title', 'metadata.generated']
      return keys
    },
    /** A list of headers for the list of reports. */
    rootHeaders () {
      return [{
        key: 'metadata.title',
        title: 'Report Name',
      },
      {
        key: 'metadata.generated',
        title: 'Date Generated',
      },
      {
        key: 'metadata.date_start_ts',
        title: 'Start Date',
      },
      {
        key: 'metadata.date_end_ts',
        title: 'End Date',
      }]
    },
    /**  */
    // MARK SORTS
    sorts () {
      let headersToConvertToSorts = []
      if (this.report && this.report.headers) {
        headersToConvertToSorts = this.report.headers
      } else {
        headersToConvertToSorts = this.rootHeaders
      }
      const sorts = headersToConvertToSorts.map(function (header) {
        return new OwalSortPredicate({
          displayName: header.title,
          comparisonFunction: function (a, b, key = header.key) {
            let ascending, descending
            const sortKeyA = objectAtKeypath(a, key) || ''
            const sortKeyB = objectAtKeypath(b, key) || ''
            descending = sortKeyA > sortKeyB
            ascending = sortKeyA < sortKeyB
            if (descending) {
              return 1
            } else if (ascending) {
              return -1
            }
            return 0
          } })
      })
      return sorts
    },
  },
  watch: {
    '$route' (to, from) {
      this.searchResults = undefined
      // react to route changes...
    },
    domainUUID () {
      this.selectReport('')
      this.reports = []
      this.fetchReports()
    },
  },
  mounted () {
    this.fetchReports()
  },
  methods: {
    /** Traverses an object until it either hits undefined or the end of the keypath. */
    objectAtKeypath,
    /** Marks a given report as open. */
    selectReport (reportId) {
      this.reportObj = null
      const path = `/report/${reportId || ':id'}`
      this.fetchReport(reportId)
      this.$nextTick(() => {
        this.$router.push({ path })
      })
    },
    /** Handles an item click. */
    itemWasClicked (item) {
      const download = this.download
      const filename = this.fileNameForReport(this.report, 'csv')
      if (item === this.backButton) {
        this.selectReport('')
      } else if (item === this.downloadButton) {
        SVC_A.get(`/api/reports/v1/reports/${this.report.id}?domain_uuid=${this.domainUUID}&format=csv`)
          .then(res => res.data)
          .then(data => {
            download(data, filename, 'text/csv')
          })
      }
    },
    /** Genreates a URL for each row based on the report. */
    generateRowURL (row) {
      let suffix = '/credential'
      let optionalSuffix = []
      if (this.searchKeys.includes('identity_id')) {
        suffix += `/${row.identity_id}`
      } else {
        if (this.searchKeys.includes('unit')) {
          optionalSuffix.push(`unit=${row.unit}`)
        }
        if (this.searchKeys.includes('surname') && !(this.searchKeys.includes('identity_id'))) {
          optionalSuffix.push(`name=${row.given_name}`)
        }
        if (this.searchKeys.includes('given_name') && !(this.searchKeys.includes('identity_id'))) {
          optionalSuffix.push(`name=${row.given_name}`)
        }
      }
      const urlPrefix = `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}`
      if (optionalSuffix.length > 0) {
        suffix += '?'
        suffix += optionalSuffix.map((searchTerm, index) => {
          if (index === 0) {
            return searchTerm
          } else {
            return `&${searchTerm}`
          }
        }).join('')
      }
      return `${urlPrefix}${suffix}`
    },
    /** --- Download related methods --- */
    /** Generates  file name for the download. */
    fileNameForReport (report, extension) {
      const concatenated = `owal-${report.metadata.title}`
      const lowerCaseName = concatenated.toLowerCase()
      const noSpaces = lowerCaseName.replace(/\s/, '-')
      const withExtension = `${noSpaces}.${extension}`
      return withExtension
    },
    /** Generates a download. */
    download (data, filename, type) {
      var file = new Blob([data], { type: type })
      if (window.navigator.msSaveOrOpenBlob) {
        // IE10+
        window.navigator.msSaveOrOpenBlob(file, filename)
      } else { // Others
        let a = document.createElement('a')
        let url = URL.createObjectURL(file)
        a.href = url
        document.body.appendChild(a)
        a.download = filename
        a.click()
        setTimeout(function () {
          document.body.removeChild(a)
          window.URL.revokeObjectURL(url)
        }, 0)
      }
    },
    /** Update data based on search results. */
    searchChanged (items, query, filters) {
      this.searchResults = items
    },
    fetchReports () {
      SVC_A.get(`/api/reports/v1/reports?domain_uuid=${this.domainUUID}`).then(res => res.data).then(data => {
        this.reports = data.reports || []
      }).catch(error => {
        console.log(error)
      })
    },
    fetchReport (reportId) {
      SVC_A.get(`/api/reports/v1/reports/${reportId}?domain_uuid=${this.domainUUID}`).then(res => res.data).then(data => {
        if (!data.ok) {
          return console.error(data.err)
        }
        this.reportObj = data.report
      }).catch(error => {
        console.log(error)
      })
    },
    /**  Extract metadata */
    contentForColumnInRow (header, row) {
      const key = header.key
      const content = objectAtKeypath(row, key)
      return content
    },
  },
  components: {
    OwalSearchBar,
    OwalTitleBar,
    ReportCell,
    VirtualList,
  },
}
</script>

<style scoped>
@media only screen {
  /** The container around the entire page */
  .owal-report__container {
    display: block;
    height: 100%;
    width: 100%;
    background: #fff;
    padding-top: 1rem;
  }

  /** The container around the report table. */
  .owal-table__container {
    /** Optimal line width is approximately 60 chars based on several articles:
   *  - https://baymard.com/blog/line-length-readability
   *  - http://webtypography.net/2.1.2
   *  - https://socialtriggers.com/perfect-content-width/
   *
   *  Since we use rem units everywhere else, 66rem is reasonable.
   **/
    max-width: 66rem;
    margin: 0 auto;
    border-radius: 0.35rem;
    box-shadow: 0 0 2px 2px rgba(50, 64, 87, 0.15);
  }

  /** The table itself. */
  .report-table {
    display: flex;
    flex-direction: column;
    border: 1px solid #eff2f7;
    border-collapse: collapse;
    width: 100%;
    height: fit-content;
    overflow: inherit;
  }

  /** Container for all of the rows. */
  tr:nth-of-type(2n) {
    background: #eff2f7;
  }

  /** Rows containing a summary of the report. */
  .report-summary {
    cursor: pointer;
    transition: all 150ms ease;
    padding: 0.5rem;
    margin: 0;
    display: flex;
    flex-grow: 1;
  }

  .report-summary:hover {
    border-left: 5px solid #324057;
    background-color: #3240570f;
    opacity: 0.8;
    padding-left: 5px;
  }

  /** Add some margins/padding to extend the clickable area to fill as much as possible. */
  .report-summary .report-name {
    margin: -0.5rem 0 -0.5rem -0.5rem;
    padding: 0.5rem 0 0.5rem 0.5rem;
  }

  .report-summary td:not(:first-of-type) {
    border-left: 1px solid rgba(50, 64, 87, 0.2);
  }

  /** Rows containing data. */
  .report-data,
  .report-name {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(1px, 1fr));
    grid-template-rows: 1fr;
    margin: 0;
    padding: 0.5rem;
    font-size: 1rem;
    width: 100%;
    transition: all 200ms ease;
    align-content: center;
    align-items: stretch;
  }

  .report-data td:not(:first-of-type) {
    border-left: 1px solid rgba(50, 64, 87, 0.2);
  }

  .report-data td {
    text-align: center;
    flex: 1;
  }

  .no-reports__container {
    padding: 0.5rem;
    background: #eff2f7;
    font-weight: bold;
    font-size: 1.2rem;
    /* border-bottom: 0.35rem solid #eff2f7; */
    border-bottom-left-radius: 0.35rem;
    border-bottom-right-radius: 0.35rem;
    text-align: center;
  }
}
</style>
